import type { Node } from '@xyflow/react';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { AutomationStepNodeData, BehaviorCaseNodeData, SegmentNodeData } from '../types/stepNodes';

export interface BranchCaseLookup {
	parentSwitchNodeId: string;
	caseIndex?: number;
	caseId?: string;
}

type BranchCaseNode = Node<SegmentNodeData | BehaviorCaseNodeData>;

function isBranchCaseNode(node: Node<BaseNodeData>): node is BranchCaseNode {
	return node.type === NODE_TYPES.SEGMENT || node.type === NODE_TYPES.BEHAVIOR_CASE;
}

function getBranchCaseIndex(node: BranchCaseNode): number | undefined {
	if (node.type === NODE_TYPES.SEGMENT) {
		return (node.data as SegmentNodeData).segmentIndex;
	}

	return (node.data as BehaviorCaseNodeData).caseIndex;
}

export function findBranchCaseNode(
	nodes: Node<BaseNodeData>[],
	{ parentSwitchNodeId, caseIndex, caseId }: BranchCaseLookup
): BranchCaseNode | undefined {
	return nodes.find(node => {
		if (!isBranchCaseNode(node)) {
			return false;
		}

		const branchNodeData = node.data as SegmentNodeData | BehaviorCaseNodeData;
		if (branchNodeData.parentSwitchNodeId !== parentSwitchNodeId) {
			return false;
		}

		if (caseId) {
			return branchNodeData.caseId === caseId;
		}

		return getBranchCaseIndex(node) === caseIndex;
	}) as BranchCaseNode | undefined;
}

export function resolveBranchCaseTemplateId(nodes: Node<BaseNodeData>[], lookup: BranchCaseLookup): string | undefined {
	const branchNode = findBranchCaseNode(nodes, lookup);
	return branchNode?.data.childTemplateId;
}

export function resolveSwitchOwnerTemplateId(nodes: Node<BaseNodeData>[], switchNodeId: string): string | undefined {
	const switchNode = nodes.find(node => node.id === switchNodeId);
	const switchNodeData = switchNode?.data as AutomationStepNodeData | undefined;
	return switchNodeData?.automationTemplate?.id;
}
